/**
 * StoreKit / Google Play Billing, via RevenueCat.
 *
 * This is the implementation behind `purchases.js`'s provider interface — the
 * thing that turns a blocked Stripe checkout on native into a real in-app
 * purchase, which is what App Store Guideline 3.1.1 and the Play Payments
 * policy require. RevenueCat rather than raw StoreKit because it covers both
 * stores, validates receipts, handles restores, and gives us one webhook to
 * grant entitlements from instead of two receipt-verification endpoints.
 *
 * **Nothing here grants anything.** A purchase resolving on the device means
 * the store took the money, not that the account should be credited — a
 * jailbroken device can fake that, and the client is never trusted. The
 * `revenuecat-webhook` edge function is what writes tokens and subscription
 * state, exactly as `stripe-webhook` does for the web. All this does is start
 * the purchase and then re-read what the server decided.
 *
 * **Identity is the load-bearing detail.** RevenueCat keys everything on its
 * `app_user_id`, and the webhook uses that to find the row to credit — so it
 * has to be the Supabase user's UUID, not RevenueCat's generated anonymous id.
 * `logIn` on sign-in and `logOut` on sign-out is what keeps that true; get it
 * wrong and purchases are credited to a ghost account that no one can log into.
 *
 * See docs/STORE_SUBMISSION.md for the store-side setup this expects.
 */

import { isNative, platform } from '../native/platform.js';
import { REVENUECAT_KEYS } from '../config/env.js';
import { registerPurchaseProvider, PRODUCT_IDS } from './purchases.js';

/** RevenueCat's code for "the user backed out of the payment sheet". */
const CANCELLED = '1';

/** Android wants to know which catalogue to look in; iOS ignores it. */
const SUBSCRIPTION = 'SUBSCRIPTION';
const NON_SUBSCRIPTION = 'NON_SUBSCRIPTION';

const SUBSCRIPTION_IDS = new Set(Object.values(PRODUCT_IDS.subscription));

/** @type {import('@revenuecat/purchases-capacitor').PurchasesPlugin | null} */
let sdk = null;

function apiKey() {
  return platform === 'ios' ? REVENUECAT_KEYS.ios : REVENUECAT_KEYS.android;
}

/** True when this build can actually transact: native shell, key configured. */
export function isBillingConfigured() {
  return isNative && Boolean(apiKey());
}

/**
 * A purchase the user backed out of is not a failure, and must not be reported
 * as one — an "error" toast after tapping Cancel reads as a charge that went
 * wrong.
 */
function wasCancelled(err) {
  return err?.code === CANCELLED || err?.userCancelled === true;
}

async function findProduct(productIdentifier) {
  const { products } = await sdk.getProducts({
    productIdentifiers: [productIdentifier],
    type: SUBSCRIPTION_IDS.has(productIdentifier) ? SUBSCRIPTION : NON_SUBSCRIPTION,
  });

  const product = products?.find((p) => p.identifier === productIdentifier);
  if (!product) {
    // Almost always a store-side configuration gap rather than a code bug: the
    // product is missing, not yet approved, or the paid-apps agreement is
    // unsigned. Say so, because "purchase failed" sends you looking in the
    // wrong place for a day.
    throw new Error(
      `${productIdentifier} is not available from the store. Check the product exists, is approved, and that this build's bundle id matches.`,
    );
  }
  return product;
}

/**
 * Ties the RevenueCat identity to the Supabase one, for the lifetime of the
 * session. Runs on the current user at startup and follows every auth change
 * after that.
 */
function trackIdentity() {
  const client = globalThis.supabaseClient;
  if (!client?.auth) return;

  const sync = async (userId) => {
    try {
      if (userId) await sdk.logIn({ appUserID: userId });
      else await sdk.logOut();
    } catch (err) {
      // Never fatal. A failed logOut leaves the previous identity in place,
      // which is a privacy annoyance, not a broken app — and throwing here
      // would take the sign-out flow down with it.
      console.warn('[ironpact] RevenueCat identity sync failed', err);
    }
  };

  client.auth.getSession()
    .then(({ data }) => sync(data?.session?.user?.id || null))
    .catch(() => {});

  // Additive: the legacy core registers its own listener, and Supabase runs
  // every subscriber.
  client.auth.onAuthStateChange((_event, session) => sync(session?.user?.id || null));
}

const provider = {
  async purchase(productIdentifier) {
    const product = await findProduct(productIdentifier);
    try {
      await sdk.purchaseStoreProduct({ product });
    } catch (err) {
      if (wasCancelled(err)) return { success: false, cancelled: true };
      throw err;
    }
    // Deliberately not reading entitlements off the returned CustomerInfo. The
    // webhook is the only thing that credits the account, and `purchases.js`
    // re-reads the server's answer next.
    return { success: true };
  },

  async restore() {
    await sdk.restorePurchases();
  },
};

export async function installRevenueCat() {
  if (!isBillingConfigured()) return;

  try {
    const module = await import('@revenuecat/purchases-capacitor');
    sdk = module.Purchases;

    const { isConfigured } = await sdk.isConfigured();
    if (!isConfigured) await sdk.configure({ apiKey: apiKey() });

    trackIdentity();
    registerPurchaseProvider(provider);
    console.info(`[ironpact] billing ready (${platform})`);
  } catch (err) {
    // Leave the provider unregistered. `purchases.js` then tells the user that
    // purchases are unavailable and points them at support, which is a far
    // better outcome than a Buy button that throws when tapped.
    console.error('[ironpact] billing unavailable', err);
  }
}
